import React, { useEffect } from 'react';
import { Form, Input, InputNumber, Switch, Button, Card, Tabs, Col, Row, Upload, message } from 'antd';
import { UploadOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../../api/axios';

const tabItems = [
    { key: 'de', label: 'Almanca (DE)' },
    { key: 'tr', label: 'Türkçe (TR)' },
    { key: 'en', label: 'İngilizce (EN)' }
];

export const OleSliderEdit: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isNew = id === 'create';
    const [form] = Form.useForm();
    const queryClient = useQueryClient();

    const { data: sliderData, isLoading } = useQuery({
        queryKey: ['adminOleSlider', id],
        queryFn: async () => {
            const response = await apiClient.get(`/admin/ole/sliders/${id}`);
            return response.data.data;
        },
        enabled: !isNew
    });

    useEffect(() => {
        if (sliderData) {
            const formattedTranslations: any = {};
            sliderData.translations?.forEach((t: any) => {
                formattedTranslations[t.languageCode] = {
                    title: t.title,
                    subtitle: t.subtitle,
                    ctaText: t.ctaText
                };
            });

            form.setFieldsValue({
                imageUrl: sliderData.imageUrl,
                ctaTarget: sliderData.ctaTarget,
                orderIndex: sliderData.orderIndex,
                isActive: sliderData.isActive,
                translations: formattedTranslations
            });
        } else if (isNew) {
            form.setFieldsValue({
                orderIndex: 0,
                isActive: true,
                translations: {}
            });
        }
    }, [sliderData, isNew, form]);

    const mutation = useMutation({
        mutationFn: async (values: any) => {
            const translations = tabItems.map(tab => {
                const lang = tab.key;
                return {
                    languageCode: lang,
                    title: values.translations?.[lang]?.title || '',
                    subtitle: values.translations?.[lang]?.subtitle || '',
                    ctaText: values.translations?.[lang]?.ctaText || ''
                };
            });

            const payload = {
                imageUrl: values.imageUrl,
                ctaTarget: values.ctaTarget,
                orderIndex: values.orderIndex,
                isActive: values.isActive,
                translations
            };

            if (isNew) {
                return apiClient.post('/admin/ole/sliders', payload);
            } else {
                return apiClient.put(`/admin/ole/sliders/${id}`, { id, ...payload });
            }
        },
        onSuccess: () => {
            message.success(isNew ? 'Slide oluşturuldu' : 'Slide güncellendi');
            queryClient.invalidateQueries({ queryKey: ['adminOleSliders'] });
            navigate('/admin/ole/sliders');
        },
        onError: (err: any) => {
            message.error(err?.response?.data?.detail || 'Kaydederken bir hata oluştu');
        }
    });

    const onFinish = (values: any) => {
        mutation.mutate(values);
    };

    const handleCustomRequest = async (options: any) => {
        const { file, onSuccess, onError } = options;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('isPublic', 'true');

        try {
            const response = await apiClient.post('/admin/media/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            const uploadedUrl = response.data.data.url;
            form.setFieldsValue({ imageUrl: uploadedUrl });
            message.success('Görsel başarıyla yüklendi.');
            onSuccess(null, file);
        } catch (err: any) {
            message.error(err.response?.data?.detail || 'Görsel yükleme hatası.');
            onError(err);
        }
    };

    return (
        <Card 
            title={isNew ? "Yeni Slide Ekle" : "Slide Düzenle"}
            extra={
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/ole/sliders')}>
                    Geri
                </Button>
            }
            loading={isLoading}
        >
            <Form 
                form={form} 
                layout="vertical" 
                onFinish={onFinish}
            >
                <Row gutter={24}>
                    <Col span={16}>
                        <Form.Item 
                            name="imageUrl" 
                            label="Görsel URL" 
                            rules={[{ required: true, message: 'Görsel URL gereklidir' }]}
                        >
                            <Input placeholder="Görsel adresini girin veya yükleyin..." />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item label="Görsel Yükle">
                            <Upload 
                                customRequest={handleCustomRequest}
                                showUploadList={false}
                                maxCount={1}
                            >
                                <Button icon={<UploadOutlined />} block>Dosya Seç</Button>
                            </Upload>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item 
                            name="ctaTarget" 
                            label="CTA Yönlendirme Hedefi"
                        >
                            <Input placeholder="Örn: #kontakt, #nachhilfeangebot veya dış link" />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item 
                            name="orderIndex" 
                            label="Sıra" 
                            rules={[{ required: true, message: 'Sıra numarası gereklidir' }]}
                        >
                            <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item 
                            name="isActive" 
                            label="Aktif" 
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                    </Col>
                </Row>

                <div style={{ marginTop: '1.5rem', borderTop: '1px solid #f0f0f0', paddingTop: '1.5rem' }}>
                    <h3>Çeviriler</h3>
                    <Tabs defaultActiveKey="de" items={tabItems.map(tab => ({
                        key: tab.key,
                        label: tab.label,
                        children: (
                            <div style={{ padding: '1rem 0' }}>
                                <Form.Item 
                                    name={['translations', tab.key, 'title']} 
                                    label="Başlık (Geniş Metin)" 
                                    rules={tab.key === 'de' ? [{ required: true, message: 'Almanca başlık zorunludur' }] : []}
                                >
                                    <Input placeholder={`${tab.label} Başlık`} />
                                </Form.Item>
                                
                                <Form.Item 
                                    name={['translations', tab.key, 'subtitle']} 
                                    label="Alt Başlık (Detay Metni)"
                                >
                                    <Input placeholder={`${tab.label} Alt Başlık`} />
                                </Form.Item>

                                <Form.Item 
                                    name={['translations', tab.key, 'ctaText']} 
                                    label="Buton Metni (CTA)"
                                >
                                    <Input placeholder={`${tab.label} Buton Metni`} />
                                </Form.Item>
                            </div>
                        )
                    }))} />
                </div>

                <Form.Item style={{ marginTop: '20px' }}>
                    <Button type="primary" htmlType="submit" loading={mutation.isPending}>
                        Kaydet
                    </Button>
                </Form.Item>
            </Form>
        </Card>
    );
};
